import type NativeArray from '@ember/array/-private/native-array';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type Flow from 'garaje/models/flow';
import type Plugin from 'garaje/models/plugin';
import type PluginInstall from 'garaje/models/plugin-install';
import type SignInField from 'garaje/models/sign-in-field';
import type SignInFieldPage from 'garaje/models/sign-in-field-page';
import type ChameleonService from 'garaje/services/chameleon';
import type StateService from 'garaje/services/state';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import type { RecordArray } from 'garaje/utils/type-utils';
import moment from 'moment-timezone';
import { all, hash } from 'rsvp';

import type WorkplaceEntriesController from './controller';

export interface WorkplaceEntriesModel {
  currentLocationId: string;
  date: string;
  filter: string;
  flows: Flow[];
  locationFlowIds: string[];
  pluginInstalls: RecordArray<PluginInstall>;
  plugins: RecordArray<Plugin>;
  query: string;
  selectedFlow: string;
  sort: string;
  customFields: NativeArray<SignInField>[];
  signInFieldPages: SignInFieldPage[];
}

export interface WorkplaceEntriesRouteParams {
  date: string;
  filter: string;
  query: string;
  selectedFlow: string;
  sort: string;
}

class WorkplaceEntriesRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare chameleon: ChameleonService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  titleToken = 'Entries';

  memoizedModel: WorkplaceEntriesModel | null = null;

  queryParams = {
    date: {
      refreshModel: true,
    },
    query: {
      refreshModel: true,
    },
    filter: {
      refreshModel: true,
    },
    selectedFlow: {
      as: 'visitor_type',
      refreshModel: true,
    },
    refresh: {
      refreshModel: true,
    },
    sort: {
      refreshModel: true,
    },
  };

  beforeModel(transition: Transition): void {
    if (this.abilities.can('start visit start trial for workplace')) {
      this.router.transitionTo('workplace.start-trial');
    }
    const dateParam = transition.to.queryParams['date'];
    const hasInvalidDateParam = isPresent(dateParam) && !moment(dateParam).isValid();
    if (hasInvalidDateParam) {
      this.router.transitionTo('workplace.entries', { queryParams: { date: null } });
    }
  }

  async model({
    date,
    query,
    filter,
    selectedFlow,
    sort,
  }: WorkplaceEntriesRouteParams): Promise<WorkplaceEntriesModel> {
    const { currentLocation } = this.state;

    if (query && this.memoizedModel?.currentLocationId === currentLocation.id) {
      return Object.assign(this.memoizedModel, { date, query, filter, selectedFlow, sort });
    }

    const currentLocationId = currentLocation.id;
    const flows = this.state.loadFlows({ reload: false, locationId: currentLocationId });

    // Parallel Requests
    const { flows: loadedFlows } = await hash({ flows });

    const byLocation = { filter: { location: currentLocationId }, page: { limit: 100 } };
    const plugins = this.store.query('plugin', byLocation);
    const pluginInstalls = this.store.query('plugin-install', byLocation);
    const locationFlowIds = currentLocation.hasMany('flows').ids();
    const signInFieldPages = await all(
      loadedFlows.filter((flow) => flow.employeeCentric).map((flow) => flow.signInFieldPage),
    );
    const customFields = await all(signInFieldPages.map((signInFieldPage) => signInFieldPage?.customFields));

    this.workplaceMetrics.trackEvent('EMPLOYEE_LOG_VIEWED');
    this.chameleon.trackDashboardPageEvent('EMPLOYEE_LOG_VIEWED');
    pluginInstalls.then((pluginInstalls) => {
      this.state.pluginInstalls = pluginInstalls.toArray();
    });

    return hash({
      currentLocationId,
      date,
      filter,
      flows,
      locationFlowIds,
      pluginInstalls,
      plugins,
      query,
      selectedFlow,
      sort,
      customFields,
      signInFieldPages,
    });
  }

  afterModel(model: WorkplaceEntriesModel): void {
    this.memoizedModel = model;
  }

  setupController(controller: WorkplaceEntriesController, model: WorkplaceEntriesModel, transition: Transition): void {
    super.setupController(controller, model, transition);
    void all([controller.loadEmployeesTask.perform()]);
    if (!controller.startDate || !controller.endDate) {
      controller.setStartAndEndTime();
    }
  }

  resetController(controller: WorkplaceEntriesController, isExiting: boolean): void {
    if (isExiting) {
      controller.clearSearch();
      void controller.pollEventReports.cancelAll();
      controller.date = '';
    }
  }
}

export default WorkplaceEntriesRoute;
